import { useEffect } from 'react'
import MediaImage from '../components/MediaImage'
import PageHeader from '../components/PageHeader'
import { closetBanner, pages } from '../data/content'

const SHOPIFY_DOMAIN = 'theitgirlguide.myshopify.com'
const STOREFRONT_TOKEN = 'fd52ef7f9527e25434474f249e0e253c'
const SCRIPT_URL = 'https://sdks.shopifycdn.com/buy-button/latest/buy-button-storefront.min.js'

/** Add more Shopify product IDs here as closet drops go live. */
const CLOSET_SHOPIFY_PRODUCTS = ['7453077209330'] as const

declare global {
  interface Window {
    ShopifyBuy?: {
      buildClient: (config: { domain: string; storefrontAccessToken: string }) => unknown
      UI: {
        onReady: (client: unknown) => Promise<{
          createComponent: (
            type: string,
            config: {
              id: string
              node: HTMLElement | null
              moneyFormat: string
              options: Record<string, unknown>
            },
          ) => void
        }>
      }
    }
  }
}

function loadBuyButtonScript(): Promise<void> {
  if (window.ShopifyBuy?.UI) return Promise.resolve()

  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_URL}"]`)
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener('error', () => reject(new Error('Shopify Buy Button failed to load')), {
        once: true,
      })
      if (window.ShopifyBuy?.UI) resolve()
      return
    }

    const script = document.createElement('script')
    script.async = true
    script.src = SCRIPT_URL
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Shopify Buy Button failed to load'))
    ;(document.head || document.body).appendChild(script)
  })
}

async function mountShopifyProduct(productId: string, node: HTMLElement) {
  await loadBuyButtonScript()
  const ShopifyBuy = window.ShopifyBuy
  if (!ShopifyBuy?.UI) return

  if (node.dataset.shopifyMounted === '1') return
  node.dataset.shopifyMounted = '1'
  node.replaceChildren()

  const client = ShopifyBuy.buildClient({
    domain: SHOPIFY_DOMAIN,
    storefrontAccessToken: STOREFRONT_TOKEN,
  })

  const btn = {
    'background-color': '#BF7D7D',
    ':hover': { 'background-color': '#AC7171' },
    ':focus': { 'background-color': '#AC7171' },
  }

  const ui = await ShopifyBuy.UI.onReady(client)
  ui.createComponent('product', {
    id: productId,
    node,
    moneyFormat: '%24%7B%7Bamount%7D%7D',
    options: {
      product: {
        buttonDestination: 'checkout',
        contents: {
          img: true,
          imgWithCarousel: false,
          button: true,
          title: true,
          price: true,
          description: true,
        },
        styles: {
          product: { 'max-width': '100%', 'margin-left': '0', 'margin-right': '0', 'margin-bottom': '0' },
          title: { 'font-family': 'Open Sans, sans-serif', 'font-weight': 'normal' },
          price: { 'font-family': 'Open Sans, sans-serif' },
          compareAt: { 'font-family': 'Open Sans, sans-serif' },
          unitPrice: { 'font-family': 'Open Sans, sans-serif' },
          description: { 'font-family': 'Open Sans, sans-serif' },
          button: btn,
        },
        text: { button: 'Buy now' },
        googleFonts: ['Open Sans'],
      },
      cart: {
        styles: { button: btn },
        text: { total: 'Subtotal', button: 'Checkout' },
      },
      toggle: {
        styles: { toggle: btn },
      },
    },
  })
}

export default function Closet() {
  const { title, dek } = pages.closet

  useEffect(() => {
    for (const productId of CLOSET_SHOPIFY_PRODUCTS) {
      const node = document.getElementById(`tigg-product-${productId}`)
      if (node) void mountShopifyProduct(productId, node)
    }
  }, [])

  return (
    <main className="py-[clamp(28px,4.5vw,60px)]">
      <PageHeader title={title} dek={dek} />

      <div data-reveal className="mx-auto mt-10 w-4/5 overflow-hidden bg-sand">
        <MediaImage
          src={closetBanner}
          size="hero"
          alt="Pieces from Sky's closet"
          className="aspect-[19/5] w-full object-cover object-center"
        />
      </div>

      <div className="mt-12 grid grid-cols-1 gap-x-5 gap-y-10 min-[480px]:grid-cols-2 sm:gap-x-[clamp(28px,3.5vw,48px)] sm:gap-y-[clamp(48px,5vw,72px)] lg:grid-cols-4">
        {CLOSET_SHOPIFY_PRODUCTS.map((productId) => (
          <article key={productId} data-reveal className="min-w-0 border-b border-rule pb-12">
            <div id={`tigg-product-${productId}`} />
          </article>
        ))}
      </div>
    </main>
  )
}
